import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AppSwitcher } from '@cbhq/app-switcher';
import { ResponsiveProps } from '@cbhq/cds-web';
import { IconButton } from '@cbhq/cds-web/buttons';
import { Box, HStack } from '@cbhq/cds-web/layout';
import { NavigationBar as CDSNavigationBar } from '@cbhq/cds-web/navigation/NavigationBar';
import { NavigationTitle } from '@cbhq/cds-web/navigation/NavigationTitle';
import { TabNavigation } from '@cbhq/cds-web/tabs/TabNavigation';

import config from ':cloud/config';
import { useAppState } from ':cloud/contexts/AppStateContext';
import { TabType, useNavigationContext } from ':cloud/contexts/NavigationContext';
import { useIsBillingUsageEnabled } from ':cloud/hooks/featureFlags/useIsBillingUsageEnabled';
import { useIsEarningsEnabled } from ':cloud/hooks/featureFlags/useIsEarningsEnabled';
import { useIsOAuthEnabled } from ':cloud/hooks/featureFlags/useIsOAuthEnabled';
import { useIsPayAsYouGoEnabled } from ':cloud/hooks/featureFlags/useIsPayAsYouGoEnabled';
import useHasExperiment from ':cloud/hooks/useHasExperiment';
import { useIdentityNavigationExperiment } from ':cloud/hooks/useIdentityNavigationExperiment';
import { useIdentityTarget } from ':cloud/hooks/useIdentityTarget';
import useLoadedLegacyAPITokens from ':cloud/hooks/useLoadedLegacyAPITokens';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGetOrganization } from ':cloud/queries/OrganizationQueries/useGetOrganization';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { useGetUserPermissions } from ':cloud/queries/UserQueries/useGetUserPermissions';
import { AppRoute } from ':cloud/utils/routes';
import { HamburgerNav } from ':cloud/widgets/navigation/HamburgerNav';
import HelpDropdown from ':cloud/widgets/navigation/HelpDropdown';
import { NavigationBar } from ':cloud/widgets/navigation/NavigationBar';
import { NotificationsDropdown } from ':cloud/widgets/navigation/NotificationsDropdown';

import { ProfileDropdown } from './ProfileDropdown';

const responsiveConfigEndContent: ResponsiveProps = {
  phone: { spacingEnd: 0 },
  tablet: { spacingBottom: 0 },
  desktop: { spacingBottom: 0 },
};

const responsiveConfigNavBar: ResponsiveProps = {
  phone: { spacingStart: 0.5 },
  tablet: { spacingBottom: 0 },
  desktop: { spacingBottom: 0 },
};

/** order of tabs matters */
const headerTabs: Record<string, TabType[]> = {
  settings: [
    {
      id: 'organization',
      label: 'Organization',
    },
    {
      id: 'billing',
      label: 'Billing',
    },
  ],
  finance: [
    {
      id: 'billing',
      label: 'Billing',
    },
    {
      id: 'earnings',
      label: 'Earnings',
    },
  ],
  access: [
    {
      id: 'teams',
      label: 'Teams',
    },
    {
      id: 'api',
      label: 'API keys',
    },
    {
      id: 'legacy-api',
      label: 'Legacy API management',
    },
  ],
  admin: [
    {
      id: 'protocols',
      label: 'Protocols',
    },
    {
      id: 'clusters',
      label: 'Clusters',
    },
    {
      id: 'organizations',
      label: 'Organizations',
    },
  ],
  projectDetails: [
    {
      id: 'overview',
      label: 'Overview',
    },
    {
      id: 'monitor',
      label: 'Monitor',
    },
    {
      id: 'manage',
      label: 'Manage',
    },
  ],
  billing: [
    {
      id: 'details',
      label: 'Details',
    },
    {
      id: 'usage',
      label: 'Usage',
    },
  ],
};

const accessTabItems: {
  id: string;
  label: string;
}[] = [
  {
    id: AppRoute.Access.API,
    label: 'API keys',
  },
  {
    id: AppRoute.Access.OAuth,
    label: 'OAuth',
  },
];

export function TopNav() {
  const { selectedProject } = useAppState();
  const { activePage, activeTab, projectId } = useNavigationContext();
  const { user, activeOrg } = useGetUser();
  const { truncatedName: orgName } = useGetOrganization(activeOrg?.organizationId);
  const history = useHistory();
  const location = useLocation();
  const pathArray = location.pathname.split('/');
  const { isPhone } = useSimpleBreakpoints();
  const legacyAPItokens = useLoadedLegacyAPITokens();
  const isLegacyAPIManagementEnabled =
    useHasExperiment('nov_2023_legacy_api_management', 'treatment') ||
    Boolean(legacyAPItokens?.length);
  const { permissions } = useGetUserPermissions(activeOrg?.organizationId);
  const isOAuthEnabled = useIsOAuthEnabled();
  const isNewBaseProjectRoute = location.pathname.includes(AppRoute.Projects.NewBase);
  const isOAuthUpsertClientRoute =
    location.pathname.includes(AppRoute.Access.OAuthCreate) ||
    location.pathname.includes(AppRoute.Access.OAuthEdit);
  const isAccessPage =
    isOAuthEnabled &&
    !isOAuthUpsertClientRoute &&
    (location.pathname.includes(AppRoute.Access.API) ||
      location.pathname.includes(AppRoute.Access.OAuth));
  const isEarningsEnabled = useIsEarningsEnabled();
  const isPayAsYouGoEnabled = useIsPayAsYouGoEnabled();
  const isBillingUsageEnabled = useIsBillingUsageEnabled();

  const currentProject = useMemo(
    () => (projectId ? selectedProject : undefined),
    [projectId, selectedProject],
  );

  const showMonitoringTab = useMemo(() => {
    return (
      currentProject?.type === 'staking_api' ||
      currentProject?.type === 'base' ||
      currentProject?.type === 'waas_consumer'
    );
  }, [currentProject?.type]);

  const tabs = useMemo(() => {
    if (isNewBaseProjectRoute) {
      return undefined;
    }
    if (activePage.id === 'projects' && projectId) {
      return headerTabs.projectDetails;
    }
    if (activePage.id === 'billing' && !isBillingUsageEnabled) {
      return undefined;
    }
    return headerTabs[activePage.id];
  }, [activePage.id, isBillingUsageEnabled, isNewBaseProjectRoute, projectId]);

  const handleBackPress = useCallback(() => {
    if (location.pathname.includes('participate')) {
      history.push(AppRoute.Participate.Home);
    } else if (currentProject?.type === 'base') {
      /* if a new base project was created we do not want
      to return to the previous page because it would be
      the network selection page */
      history.push(AppRoute.Projects.Home);
    } else {
      history.go(-1);
    }
  }, [currentProject?.type, history, location.pathname]);

  const showBackButton = useMemo(
    (): boolean =>
      (!isPhone &&
        pathArray.length > 2 &&
        !isNewBaseProjectRoute &&
        (location.pathname.includes(AppRoute.Participate.Home) ||
          location.pathname.includes(AppRoute.Projects.Home))) ||
      location.pathname.includes(AppRoute.Products.WaasConsumer) ||
      isOAuthUpsertClientRoute,
    [isOAuthUpsertClientRoute, isNewBaseProjectRoute, isPhone, location.pathname, pathArray.length],
  );

  const startContent = showBackButton && (
    <Box spacingStart={2}>
      <IconButton name="backArrow" onPress={handleBackPress} />
    </Box>
  );
  const target = useIdentityTarget();

  const endContent = (
    <HStack alignItems="center" responsiveConfig={responsiveConfigEndContent}>
      <HStack gap={1}>
        {!isPhone && <HelpDropdown />}
        {!isPhone && (
          <AppSwitcher
            projectId={config.appSwitcherProjectId}
            action="web-CloudConsole-LoggedInHeader"
            target={target}
          />
        )}
        <NotificationsDropdown />
        {!isPhone && <ProfileDropdown />}
      </HStack>
    </HStack>
  );

  const filteredTabs = tabs?.filter((filteredTabId: TabType): boolean => {
    switch (filteredTabId.id) {
      /** admin page */
      case 'protocols':
      case 'clusters':
      case 'organizations':
        return true;

      /** access page */
      case 'api':
        return true;
      case 'legacy-api':
        return isLegacyAPIManagementEnabled || user.isInternalUser;

      /** settings page */
      case 'organization':
        return true;
      case 'billing':
        return permissions.manageBilling && isPayAsYouGoEnabled && isBillingUsageEnabled;

      /** earnings page */
      case 'earnings':
        return isEarningsEnabled;

      /** project details page */
      case 'overview':
        return true;
      /** monitoring tab */
      case 'monitor':
        return showMonitoringTab;

      /** manage tab */
      case 'manage':
        return true;
      case 'details':
        return permissions.manageBilling && isPayAsYouGoEnabled && isBillingUsageEnabled;
      case 'usage':
        return permissions.manageBilling && isPayAsYouGoEnabled && isBillingUsageEnabled;
      default:
        return false;
    }
  });

  const bottomContent = filteredTabs && (
    <TabNavigation
      spacingStart={isPhone ? 1 : 2}
      tabs={filteredTabs}
      value={activeTab as string}
      onChange={(tabId) => {
        if (activePage.id === 'projects' || activePage.id === 'project-dashboard') {
          history.replace(`/${pathArray[1]}/${projectId}/${tabId}`);
          return;
        }
        history.replace(`/${pathArray[1]}/${tabId}`);
      }}
    />
  );

  const accessTabs = (
    <TabNavigation
      tabs={accessTabItems}
      value={location.pathname || accessTabItems[0].id}
      onChange={(accessTabId: string) => {
        history.push(accessTabId);
      }}
    />
  );

  const renderTitle = useMemo(() => {
    if (isNewBaseProjectRoute) {
      /* this is a placeholder because the base project
      isn't created and assigned a name until the user
      selects a network type */
      return 'NewProject';
    }
    if (currentProject?.name) {
      return currentProject.name;
    }
    if (activePage.id === 'dashboard') {
      return orgName;
    }

    if (activePage.id === 'waas_consumer' && location?.pathname?.includes('embedded-wallets')) {
      return 'Embedded Wallets';
    }

    if (isOAuthUpsertClientRoute) {
      // conditionally render title based on route
      return location.pathname.includes('create') ? 'Create client' : 'Edit client';
    }

    if (activePage?.pageTitle) {
      return activePage?.pageTitle;
    }

    return activePage.title;
  }, [
    activePage.id,
    activePage?.pageTitle,
    activePage.title,
    currentProject?.name,
    isNewBaseProjectRoute,
    isOAuthUpsertClientRoute,
    location.pathname,
    orgName,
  ]);

  const renderTitleTag = useMemo(() => {
    if (currentProject?.type !== 'base') {
      return undefined;
    }
    if (currentProject?.isTestnet) {
      return 'Base Node - Sepolia';
    }
    return 'Base Node - Mainnet';
  }, [currentProject?.isTestnet, currentProject?.type]);

  const showIdentitityNavigationBar = useIdentityNavigationExperiment();

  if (showIdentitityNavigationBar) {
    return (
      <NavigationBar
        bottomContent={isAccessPage ? accessTabs : bottomContent}
        handleBackPress={handleBackPress}
        showBackButton={showBackButton}
        title={renderTitle}
        titleTag={renderTitleTag}
      />
    );
  }

  return (
    <CDSNavigationBar
      start={startContent}
      end={endContent}
      bottom={isAccessPage ? accessTabs : bottomContent}
    >
      {/* increase hit box on mobile */}
      <HStack
        alignItems="center"
        justifyContent="space-between"
        responsiveConfig={responsiveConfigNavBar}
        width="100%"
        gap={isPhone ? 0 : 5}
      >
        {isPhone && <HamburgerNav />}
        <NavigationTitle align="start" spacingStart={showBackButton ? 0 : 2} numberOfLines={1}>
          {renderTitle}
        </NavigationTitle>
      </HStack>
    </CDSNavigationBar>
  );
}
